"use client";

// React & Three.js imports
import { useRef } from "react";
import * as THREE from "three";
import { Canvas, useLoader } from "@react-three/fiber";

// Drei components
import {
  useGLTF,
  Caustics,
  CubeCamera,
  Environment,
  OrbitControls,
  RandomizedLight,
  AccumulativeShadows,
  MeshRefractionMaterial,
  MeshTransmissionMaterial,
} from "@react-three/drei";

// Post-processing & Controls
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { useControls } from "leva";
import { RGBELoader } from "three-stdlib";

// Custom components & hooks
import { PageTitle } from "@/components/rendering/PageTitle";
import { useSceneControls } from "@/hooks/useSceneControls";

// ===================== 类型定义 =====================
interface DiamondProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
}

interface DiamondControlsConfig {
  bounces: number;
  aberrationStrength: number;
  ior: number;
  fresnel: number;
  color: string;
}

// ===================== Diamond 组件 =====================
function Diamond(props: DiamondProps) {
  const ref = useRef<THREE.Mesh>(null);
  const { nodes } = useGLTF("/assets/models/dflat.glb") as any; // TODO: 添加模型类型定义

  // 自定义环境贴图 - 独立于场景背景，用于CubeCamera
  const texture = useLoader(
    RGBELoader,
    "https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/aerodynamics_workshop_1k.hdr",
  );

  // 钻石材质控制面板
  const config = useControls("Diamond Settings", {
    bounces: { value: 3, min: 0, max: 8, step: 1 },
    aberrationStrength: { value: 0.01, min: 0, max: 0.1, step: 0.01 },
    ior: { value: 2.75, min: 0, max: 10 },
    fresnel: { value: 1, min: 0, max: 1 },
    color: "white",
  }) as DiamondControlsConfig;

  return (
    <CubeCamera resolution={256} frames={1} envMap={texture}>
      {(cubeTexture) => (
        <Caustics
          causticsOnly={false}
          backside={true}
          color={config.color}
          position={[0, -0.5, 0]}
          lightSource={[5, 5, -10]}
          worldRadius={0.1}
          ior={1.8}
          backsideIOR={1.1}
          intensity={0.1}
        >
          <mesh
            castShadow
            ref={ref}
            geometry={nodes.Diamond_1_0.geometry}
            {...props}
          >
            <MeshRefractionMaterial
              envMap={cubeTexture}
              {...config}
              toneMapped={false}
            />
          </mesh>
        </Caustics>
      )}
    </CubeCamera>
  );
}

// ===================== 透射球体控制配置类型 =====================
interface TransmissionSphereConfig {
  showTransmissionSphere: boolean;
  transmissionColor: string;
  transmissionThickness: number;
  transmissionDistortion: number;
  transmissionAnisotropy: number;
}

// ===================== 主页面组件 =====================
export default function RefractionPage() {
  // 通用场景控制
  const sceneConfig = useSceneControls({
    defaultAutoRotate: true,
    defaultAutoRotateSpeed: 0.1,
    defaultCameraFov: 45,
    defaultAmbientLightIntensity: 0.5,
    autoRotateSpeedRange: [0, 2],
    cameraFovRange: [20, 80],
    ambientLightIntensityRange: [0, 2],
  });

  // 透射球体控制面板
  const transmissionConfig = useControls("Transmission Sphere", {
    showTransmissionSphere: { value: true },
    transmissionColor: "#FF8F20",
    transmissionThickness: { value: 1, min: 0, max: 5, step: 0.1 },
    transmissionDistortion: { value: 0.25, min: 0, max: 1, step: 0.01 },
    transmissionAnisotropy: { value: 1, min: 0, max: 2, step: 0.1 },
  }) as TransmissionSphereConfig;

  return (
    <div className="w-full h-full bg-background">
      {/* 页面标题 */}
      <PageTitle
        title="钻石折射渲染"
        subtitle="MeshRefractionMaterial + Caustics"
        subtitleClassName="text-muted-foreground"
      />

      {/* 3D 渲染场景 */}
      <Canvas
        shadows
        camera={{
          position: [-5, 0.5, 5],
          fov: sceneConfig.cameraFov,
        }}
      >
        {/* 场景背景 */}
        <color attach="background" args={["#f0f0f0"]} />

        {/* 光照系统 - 复杂光照适合折射效果 */}
        <ambientLight intensity={sceneConfig.ambientLightIntensity * Math.PI} />
        <spotLight decay={0} position={[5, 5, -10]} angle={0.15} penumbra={1} />
        <pointLight decay={0} position={[-10, -10, -10]} />

        {/* ================== 渲染对象 ================== */}

        {/* 主要展示：钻石折射效果 */}
        <Diamond rotation={[0, 0, 0.715]} position={[0, -0.175 + 0.5, 0]} />

        {/* 辅助展示：透射球体（可选） */}
        {transmissionConfig.showTransmissionSphere && (
          <Caustics
            causticsOnly={false}
            backside={false}
            color={transmissionConfig.transmissionColor}
            position={[0, -0.5, 0]}
            lightSource={[5, 5, -10]}
            worldRadius={0.01}
            ior={1.2}
            intensity={0.005}
          >
            <mesh castShadow receiveShadow position={[-2, 0.5, -1]} scale={0.5}>
              <sphereGeometry args={[1, 64, 64]} />
              <MeshTransmissionMaterial
                resolution={1024}
                distortion={transmissionConfig.transmissionDistortion}
                color={transmissionConfig.transmissionColor}
                thickness={transmissionConfig.transmissionThickness}
                anisotropy={transmissionConfig.transmissionAnisotropy}
              />
            </mesh>
          </Caustics>
        )}

        {/* 对比参考：标准材质球体 */}
        <mesh castShadow receiveShadow position={[1.75, 0.25, 1]} scale={0.75}>
          <sphereGeometry args={[1, 64, 64]} />
          <meshStandardMaterial color="hotpink" />
        </mesh>

        {/* ================== 场景系统 ================== */}

        {/* 阴影系统 */}
        <AccumulativeShadows
          temporal
          frames={100}
          color="orange"
          colorBlend={2}
          toneMapped={true}
          alphaTest={0.7}
          opacity={1}
          scale={12}
          position={[0, -0.5, 0]}
        >
          <RandomizedLight
            amount={8}
            radius={10}
            ambient={0.5}
            position={[5, 5, -10]}
            bias={0.001}
          />
        </AccumulativeShadows>

        {/* 环境贴图 */}
        <Environment files="/assets/textures/aerodynamics_workshop_1k.hdr" />

        {/* 相机控制 */}
        <OrbitControls
          makeDefault
          autoRotate={sceneConfig.autoRotate}
          autoRotateSpeed={sceneConfig.autoRotateSpeed}
          minPolarAngle={0}
          maxPolarAngle={Math.PI / 2}
        />

        {/* 后处理效果 - Bloom增强折射光泽效果 */}
        <EffectComposer>
          <Bloom luminanceThreshold={1} intensity={2} levels={9} mipmapBlur />
        </EffectComposer>
      </Canvas>
    </div>
  );
}
